import React, { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import "./BoysBranch.css";
import { useNavigate } from "react-router-dom";

const FACILITIES = [
  { icon: "🏫", label: "Spacious Classrooms" },
  { icon: "🔬", label: "Science & Computer Labs" },
  { icon: "📚", label: "Library" },
  { icon: "⚽", label: "Sports Ground" },
  { icon: "🚌", label: "Transport Facility" },
];

const ADDRESS = "5FC7+3X9, Road, Naveed Town, Multan, Pakistan";
const PHONE_DISPLAY = "+92 304 7630462";
const PHONE_LINK = "+923047630462";
const EMAIL = "academicgroupofschools@gmail.com";

const MAP_EMBED_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13622.229915418517!2d71.4653778!3d30.1701592!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x393b3229a52ab4ad%3A0x123456789abcdef!2s5FC7%2B3X9%2C%20Road%2C%20Naveed%20Town%2C%20Multan%2C%20Pakistan!5e0!3m2!1sen!2s!4v1694936502045!5m2!1sen!2s";

const DIRECTIONS_URL = `https://www.google.com/maps?q=${encodeURIComponent(
  ADDRESS
)}`;

/* ---------- Animation variants ---------- */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9, rotateX: 12 },
  show: {
    opacity: 1,
    scale: 1,
    rotateX: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

/* ---------- 3D tilt wrapper ---------- */
function TiltCard({ children, className = "" }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), {
    stiffness: 180,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), {
    stiffness: 180,
    damping: 18,
  });

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    mx.set(px - 0.5);
    my.set(py - 0.5);
    ref.current.style.setProperty("--mx", `${px * 100}%`);
    ref.current.style.setProperty("--my", `${py * 100}%`);
  };

  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.li
      ref={ref}
      className={className}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      variants={scaleIn}
    >
      {children}
    </motion.li>
  );
}

function BoysBranch() {
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();

  return (
    <main className="bb">
      {/* Scroll progress */}
      <motion.div className="bb-progress" style={{ scaleX: scrollYProgress }} />

      {/* Hero */}
      <motion.header
        className="bb-hero"
        initial="hidden"
        animate="show"
        variants={stagger}
      >
        <motion.div className="bb-hero__inner" variants={stagger}>
          <motion.span className="bb-hero__badge" variants={fadeUp}>
            Academic Group of Schools
          </motion.span>

          <motion.h1
            className="bb-hero__title"
            variants={fadeUp}
          >
            Boys Branch
          </motion.h1>

          <motion.p className="bb-hero__text" variants={fadeUp}>
            Welcome to the Boys Branch campus. We are committed to providing
            quality education and a supportive environment for our students.
          </motion.p>

          <motion.button
            type="button"
            className="bb-btn bb-btn--light"
            onClick={() => navigate("/ApplyForm")}
            variants={fadeUp}
            whileTap={{ scale: 0.96 }}
          >
            Apply Now
          </motion.button>
        </motion.div>
      </motion.header>

      <div className="bb-content">
        {/* About */}
        <motion.section
          className="bb-section"
          aria-labelledby="bb-about"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <h2 id="bb-about" className="bb-section__title">
            About Our Branch
          </h2>
          <p className="bb-section__text">
            Our Boys Branch is equipped with modern classrooms, science labs,
            computer labs, sports facilities, and a dedicated faculty to ensure
            the best learning experience. We focus on academics, character
            building, and extracurricular activities to help students grow in
            all aspects of life.
          </p>
        </motion.section>

        {/* Facilities */}
        <motion.section
          className="bb-section"
          aria-labelledby="bb-facilities"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <h2 id="bb-facilities" className="bb-section__title">
            Facilities
          </h2>
          <motion.ul
            className="bb-facilities"
            variants={stagger}
          >
            {FACILITIES.map((item) => (
              <TiltCard key={item.label} className="bb-facilities__item">
                <span className="bb-facilities__icon" aria-hidden="true">
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </TiltCard>
            ))}
          </motion.ul>
        </motion.section>

        {/* Contact */}
        <motion.section
          className="bb-section"
          aria-labelledby="bb-contact"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <h2 id="bb-contact" className="bb-section__title">
            Contact Information
          </h2>

          <motion.div
            className="bb-contact"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div className="bb-contact__card" variants={fadeUp}>
              <span className="bb-contact__icon" aria-hidden="true">
                📍
              </span>
              <div>
                <h3 className="bb-contact__label">Address</h3>
                <p className="bb-contact__value">{ADDRESS}</p>
              </div>
            </motion.div>

            <motion.div className="bb-contact__card" variants={fadeUp}>
              <span className="bb-contact__icon" aria-hidden="true">
                📞
              </span>
              <div>
                <h3 className="bb-contact__label">Phone</h3>
                <p className="bb-contact__value">
                  <a href={`tel:${PHONE_LINK}`}>{PHONE_DISPLAY}</a>
                </p>
              </div>
            </motion.div>

            <motion.div className="bb-contact__card" variants={fadeUp}>
              <span className="bb-contact__icon" aria-hidden="true">
                ✉️
              </span>
              <div>
                <h3 className="bb-contact__label">Email</h3>
                <p className="bb-contact__value">
                  <a className="bb-contact__email" href={`mailto:${EMAIL}`}>
                    {EMAIL}
                  </a>
                </p>
              </div>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Map */}
        <motion.section
          className="bb-section"
          aria-labelledby="bb-map"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <h2 id="bb-map" className="bb-section__title">
            Find Us on Map
          </h2>
          <div className="bb-map">
            <iframe
              title="Boys Branch Location"
              src={MAP_EMBED_SRC}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <motion.a
            href={DIRECTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bb-btn bb-btn--primary"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            📍 Get Directions
          </motion.a>
        </motion.section>
      </div>
    </main>
  );
}

export default BoysBranch;